import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaFilter } from 'react-icons/fa';
import ProductGrid from './ProductGrid';
import productosService from '../../services/productosService';
import analyticsService from '../../services/analyticsService';
import './Products.css';

const ALL_TYPES = ['Tablero', 'Cartas', 'Dados', 'Rol', 'Miniaturas'];

const Products = () => {
  // El buscador del encabezado navega a /productos?search=..., así que el
  // término tiene que venir de la URL y no solo del input local. También hace
  // el resultado enlazable y compartible.
  const [searchParams] = useSearchParams();
  const searchFromUrl = searchParams.get('search') || '';

  const [selectedTypes, setSelectedTypes] = useState([]);
  const [searchInput, setSearchInput] = useState(searchFromUrl); // Estado del input
  const [searchTerm, setSearchTerm] = useState(searchFromUrl); // Estado para la búsqueda real (con debounce)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false); // Loading específico para búsqueda
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12, // Reducido para probar la paginación
    total: 0,
    totalPages: 1,
  });
  const [productos, setProductos] = useState([]);
  const searchInputRef = useRef(null); // Ref para mantener el foco en el input
  const [wasSearching, setWasSearching] = useState(false); // Para saber si estábamos buscando

  const cargarProductos = useCallback(async (isSearch = false) => {
    try {
      // Si es una búsqueda, usar loading específico, sino el general
      if (isSearch) {
        setSearching(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Obtener productos con filtros y paginación
      const data = await productosService.getProductos({
        page: pagination.page,
        limit: pagination.limit,
        tipo: selectedTypes.join(','),
        search: searchTerm,
      });

      setProductos(data.productos);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total,
        totalPages: Math.ceil(data.pagination.total / prev.limit),
      }));

      if (searchTerm && searchTerm.trim()) {
        // Reporte diferido: con trackSearch a secas, cada pausa de 500 ms al
        // teclear quedaba guardada como una búsqueda distinta ("Ps", "Psi",
        // "Psicolo"...), inflando el conteo y ensuciando el informe.
        analyticsService.trackSearchSettled(searchTerm.trim(), data.pagination.total);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
      setError('Error al cargar el catálogo. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
      setSearching(false);
      // Restaurar el foco si estábamos buscando
      if (wasSearching && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    }
  }, [pagination.page, pagination.limit, selectedTypes, searchTerm, wasSearching]);

  // Función para aplicar filtros y cargar productos
  const aplicarFiltros = useCallback(() => {
    // Reiniciar a la primera página cuando cambian los filtros
    setPagination(prev => ({ ...prev, page: 1 }));
    cargarProductos();
  }, [cargarProductos]);

  // Cargar productos cuando cambia la página o tipos seleccionados
  useEffect(() => {
    cargarProductos();
  }, [pagination.page, selectedTypes.join(',')]);

  // Si la URL cambia estando ya en /productos —el caso típico es buscar otra
  // cosa desde el encabezado sin salir de la tienda— hay que arrastrar el
  // input y el término, porque el estado inicial solo se lee al montar.
  useEffect(() => {
    setSearchInput(searchFromUrl);
    setSearchTerm(searchFromUrl);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [searchFromUrl]);

  // Debounce para la búsqueda: actualiza searchTerm después de 500ms sin escribir
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Cargar productos cuando cambia searchTerm (búsqueda con debounce)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Reiniciar a la primera página y buscar
    setPagination(prev => ({ ...prev, page: 1 }));
    cargarProductos(true); // Indicar que es una búsqueda
  }, [searchTerm]);

  const handlePageChange = newPage => {
    // Solo actualizar si la página es diferente a la actual
    if (newPage !== pagination.page) {
      setPagination(prev => ({
        ...prev,
        page: Math.max(1, Math.min(newPage, prev.totalPages)),
      }));
      window.scrollTo(0, 0);
    }
  };

  const handleTypeChange = tipo => {
    setSelectedTypes(prev =>
      prev.includes(tipo) ? prev.filter(t => t !== tipo) : [...prev, tipo]
    );
  };

  // Manejador para el input de búsqueda
  const handleSearchChange = e => {
    setSearchInput(e.target.value);
    setWasSearching(true); // Indicar que estamos buscando
  };

  // Manejador para el botón de aplicar filtros (búsqueda inmediata)
  const handleApplyFilters = () => {
    setWasSearching(true);
    setSearchTerm(searchInput);
    if (selectedTypes.length > 0) {
      analyticsService.trackFilterChange('tipo', selectedTypes.join(','));
    }
    aplicarFiltros();
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;
    return (
      <div className="flex justify-center items-center gap-2 mt-4">
        <button
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={pagination.page === 1}
          className="px-4 py-2 border border-df-border rounded disabled:opacity-50 text-df-text hover:bg-df-surface2 transition-colors"
        >
          Anterior
        </button>

        <div className="flex gap-1">
          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
            let pageNum;
            if (pagination.totalPages <= 5) {
              pageNum = i + 1;
            } else if (pagination.page <= 3) {
              pageNum = i + 1;
            } else if (pagination.page >= pagination.totalPages - 2) {
              pageNum = pagination.totalPages - 4 + i;
            } else {
              pageNum = pagination.page - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`w-10 h-10 flex items-center justify-center rounded ${
                  pagination.page === pageNum
                    ? 'bg-df-accent-strong text-white'
                    : 'border border-df-border text-df-text hover:bg-df-surface2'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={pagination.page === pagination.totalPages}
          className="px-4 py-2 border border-df-border rounded disabled:opacity-50 text-df-text hover:bg-df-surface2 transition-colors"
        >
          Siguiente
        </button>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* h1 y no h2: el catálogo es una página por derecho propio y no tenía
          ningún encabezado de primer nivel, así que para un buscador era una
          página sin tema. El texto lleva la palabra por la que se busca
          ("juegos de mesa"), no una etiqueta interna como "Productos". */}
      <h1 className="text-2xl font-bold text-center mb-8 text-df-text font-cinzel">
        Juegos de mesa disponibles
      </h1>

      {/* Mostrar loading solo en carga inicial */}
      {loading && productos.length === 0 && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-df-accent"></div>
          <p className="mt-2 text-df-muted">Cargando productos...</p>
        </div>
      )}

      {/* Mostrar error */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error}</p>
          <button
            onClick={cargarProductos}
            className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Contenido principal - siempre visible después de carga inicial */}
      {(productos.length > 0 || (!loading && productos.length === 0)) && (
        <>
          {/* Información del catálogo y paginación */}
          <div className="mb-6 text-center">
            <p className="text-df-muted mb-2">
              Mostrando {(pagination.page - 1) * pagination.limit + 1} -
              {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}{' '}
              productos
              {searching && <span className="ml-2 text-sm text-df-cyan">(buscando...)</span>}
            </p>

            {renderPagination()}
          </div>

          {/* Botón de filtros - siempre visible */}
          <div className="mb-4 flex justify-start">
            <button
              onClick={() => setIsFiltersOpen(prev => !prev)}
              className="flex items-center gap-2 bg-df-surface hover:bg-df-surface2 border border-df-border text-df-text px-4 py-2 rounded text-sm font-medium transition-colors"
            >
              <FaFilter />
              {isFiltersOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
            </button>
          </div>
        </>
      )}

      {(productos.length > 0 || (!loading && productos.length === 0)) && (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Panel lateral de filtros */}
          {isFiltersOpen && (
            <aside className="w-full lg:w-1/4 bg-df-surface border border-df-border p-4 rounded-lg shadow-lg filters-sidebar">
              {/* Filtro por nombre */}
              <div className="mb-6">
                <label htmlFor="search" className="block text-lg font-semibold mb-2 text-df-text">
                  Buscar por nombre:
                </label>
                <div className="search-container">
                  <input
                    ref={searchInputRef}
                    id="search"
                    type="text"
                    placeholder="Buscar juegos..."
                    value={searchInput}
                    onChange={handleSearchChange}
                    onKeyPress={e => e.key === 'Enter' && handleApplyFilters()}
                    onBlur={() => setWasSearching(false)}
                    className="flex-1 px-4 py-2 border border-df-border bg-df-surface2 text-df-text rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-df-accent placeholder:text-df-muted"
                  />
                  <button
                    onClick={handleApplyFilters}
                    className="bg-df-accent-strong text-white px-4 py-2 rounded hover:bg-df-accent-deep transition-colors"
                  >
                    Buscar
                  </button>
                </div>
              </div>

              {/* Filtro por tipo */}
              <div className="mb-6">
                <label className="text-lg font-semibold mb-2 text-df-text">Filtrar por tipo:</label>
                <div className="flex flex-col gap-2">
                  {ALL_TYPES.map(tipo => (
                    <label key={tipo} className="flex items-center space-x-2 text-df-text">
                      <input
                        type="checkbox"
                        value={tipo}
                        checked={selectedTypes.includes(tipo)}
                        onChange={() => handleTypeChange(tipo)}
                        className="accent-df-accent"
                      />
                      <span>{tipo}</span>
                    </label>
                  ))}
                </div>
              </div>
            </aside>
          )}

          {/* Grid de productos */}
          <main className={`flex-1 transition-all ${searching ? 'opacity-70' : 'opacity-100'}`}>
            {searching && productos.length > 0 && (
              <div className="text-center py-2 mb-4 bg-df-surface border border-df-border rounded">
                <span className="text-sm text-df-cyan">Buscando productos...</span>
              </div>
            )}
            <ProductGrid products={productos} className="p-6" />

            {/* Paginador inferior */}
            {productos.length > 0 && (
              <div className="mb-6">
                {renderPagination()}
              </div>
            )}

            {/* Mostrar mensaje si no hay productos */}
            {!loading && !searching && productos.length === 0 && (
              <div className="text-center py-12">
                <p className="text-df-muted text-lg">
                  No se encontraron productos con los filtros seleccionados.
                </p>
                <button
                  onClick={() => {
                    setSelectedTypes([]);
                    setSearchInput('');
                    setSearchTerm('');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="mt-4 text-df-accent hover:text-df-accent-hover font-medium"
                >
                  Limpiar filtros
                </button>
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default Products;
